import { Component, OnInit, OnDestroy, EventEmitter, Output, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, combineLatest, forkJoin, iif, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, scan, startWith, switchMap, tap } from 'rxjs/operators';
import { SubSink } from 'subsink2';
import { AppConfig } from '../../visa';
import { IDropdown, IDropDownEntityContainer } from '../../models/search-model';
import { DropDownService } from '../../services/drop-down-service';

@Component({
  selector: 'app-customer-dropdown-simple',
  templateUrl: './customer-dropdown-simple.component.html',
  styleUrls: ['./customer-dropdown-simple.component.css']
})
export class CustomerDropdownSimpleComponent implements OnInit, OnDestroy {
  @Input() control!: FormControl;
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() fieldLabel = 'Customer';
  @Input() resource: string;
  @Output() selectionChange = new EventEmitter();

  private subs = new SubSink();
  filterControl = new FormControl();
  formValue$: Observable<string>;
  finalDropdown$: Observable<IDropdown[]>;

  constructor(private ddService: DropDownService) { }

  ngOnInit() {
    const initialDropdown$ = this.ddService.get(this.resource, this.getBasePayload(), AppConfig.apiVisa).pipe(
      map(resp => resp?.data?.map(this.mapContainerToDropDown)),
      catchError(err => {
        return of([] as IDropdown[]);
      })
    );
    const initialValue$ = this.ddService.get(this.resource, this.getInitPayload(), AppConfig.apiVisa).pipe(
      map(resp => resp?.data?.map(this.mapContainerToDropDown)),
      catchError(err => {
        return of([] as IDropdown[]);
      })
    );
    const both$ = forkJoin([initialDropdown$, initialValue$]).pipe(
      map(([a, b]) => [...a, ...b]),
    );
    const computedInitDropdown$ = iif(() => this.control.value === null, initialDropdown$, both$);
    const formValue$ = this.filterControl.valueChanges.pipe(
      startWith(<string>null), // this string cast is just to avoid the linter from complaining
      debounceTime(300),
      distinctUntilChanged(),
    );
    this.finalDropdown$ = combineLatest([computedInitDropdown$, formValue$]).pipe(
      switchMap(([customerList, search]) => {
        if (!search) return of({ og: customerList, filt: null, new: false });
        const filter = customerList.filter(c => c.viewValue.toLocaleLowerCase().includes(search.toLocaleLowerCase()));
        if (filter.length) return of({ og: customerList, filt: filter, new: false });
        const pld = {
          ...this.getBasePayload(),
          fitlerConditions: [{ filterColumn: `${this.resource}_code`, filterOperator: "=", filterValues: [search] }]
        };
        return this.ddService.get(this.resource, pld, AppConfig.apiVisa).pipe(
          map(resp => resp?.data?.map(this.mapContainerToDropDown)),
          map(options => ({ og: customerList, filt: options, new: true })),
          catchError(err => {
            return of({ og: customerList, filt: [] as IDropdown[], new: false });
          })
        )
      }),
      scan((acc, cur) => {
        // accumulate the new search values in the original list
        const initial = acc.acc.length ? acc.acc : cur.og;
        const runTot = cur.new
          ? [...initial, ...cur.filt]
          : [...initial];
        const newFinal = cur?.filt
          ? [...cur.filt]
          : runTot
        return {
          acc: runTot,
          final: newFinal
        }
      }, { acc: [], final: [] }),
      map(acc => acc.final)
    );
  }

  getBasePayload = () => ({ limit: 1000, orderBy: `${this.resource}_code`, order: 'ASC' });

  getInitPayload = () => ({
    ...this.getBasePayload(),
    filterConditions: [{ filterColumn: "guid", filterOperator: "=", filterValues: [this.control?.value] }]
  })

  mapContainerToDropDown = (container: IDropDownEntityContainer) => (
    {
      viewValue: `${container[`${this.resource}_code`]?.toString()} | ${container.name?.toString()}`,
      value: container.guid?.toString()
    } as IDropdown
  );

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}